<template>
  <div>
    <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
      <span>Loading...</span>
    </div>
    <div v-else-if="errorMessage" class="text-red-500 mb-4">{{ errorMessage }}</div>
    <div v-else>
      <h4 class="text-lg font-semibold text-[#00423D] mb-4">Outputs</h4>
      <p class="text-gray-700 mb-4">
        Outputs are the direct, quantifiable results of your program activities, such as the number of workshops held, participants trained, or services provided. These are the immediate products of your program's work.
      </p>
      <QuestionField
        v-model="answerState.userAnswer"
        :question="questionText"
        placeholder="Enter your program's outputs..."
        :error-message="fieldErrorMessage"
      />
      <p v-if="errorMessage" class="text-red-500">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, defineExpose } from 'vue';
import QuestionField from '@/components/QuestionField.vue';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';

const props = defineProps({
  programId: {
    type: [String, Number],
    required: true
  }
});

const isLoading = ref(true);
const errorMessage = ref('');
const questionText = ref('');
const answerState = reactive({ userAnswer: '' });
const lastSavedValue = ref('');
const questionId = ref('');
const categoryId = ref('');
const authStore = useAuthStore();
const fieldErrorMessage = ref('');

const fetchQuestionData = async () => {
  try {
    const res = await fetch(`${global.API_URL}/questions/measuring_impact_outputs`, {
      headers: { Authorization: `Bearer ${authStore.user.accessToken}` }
    });
    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    const data = await res.json();
    questionText.value = data.question.title || '';
    questionId.value = data.question.id;
    categoryId.value = data.question.category_id;
  } catch (error) {
    errorMessage.value = 'Failed to load question data.';
  }
};

const fetchAnswer = async () => {
  try {
    const res = await fetch(`${global.API_URL}/answers/core_program/${props.programId}?question_id=${questionId.value}`, {
      headers: { Authorization: `Bearer ${authStore.user.accessToken}` }
    });
    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    const data = await res.json();
    answerState.userAnswer = data.answer?.answer || '';
    lastSavedValue.value = answerState.userAnswer;
  } catch (error) {
    errorMessage.value = 'Failed to load your previous answer.';
  }
};

const saveAnswer = async () => {
  if (answerState.userAnswer === lastSavedValue.value) return true;
  try {
    const res = await fetch(`${global.API_URL}/answers/core_program/${props.programId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        question_id: questionId.value,
        answer: answerState.userAnswer,
        category_id: categoryId.value
      })
    });
    if (!res.ok) throw new Error('Failed to save answer');
    lastSavedValue.value = answerState.userAnswer;
    errorMessage.value = '';
    return true;
  } catch (error) {
    errorMessage.value = 'Failed to save your answer.';
    return false;
  }
};

const validate = async () => {
  if (!answerState.userAnswer || answerState.userAnswer.trim() === '') {
    fieldErrorMessage.value = 'This is required';
    return false;
  }
  fieldErrorMessage.value = '';
  return await saveAnswer();
};

watch(() => answerState.userAnswer, (val) => {
  if (val && val.trim() !== '') {
    fieldErrorMessage.value = '';
  }
});

// Watch for programId changes and refetch data
watch(() => props.programId, async (newProgramId, oldProgramId) => {
  if (newProgramId && newProgramId !== oldProgramId) {
    await loadData();
  }
}, { immediate: false });

const loadData = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  fieldErrorMessage.value = '';
  // Reset answer state when switching programs
  answerState.userAnswer = '';
  lastSavedValue.value = '';
  
  await fetchQuestionData();
  if (questionId.value) {
    await fetchAnswer();
  }
  isLoading.value = false;
};

defineExpose({ validate, saveAnswer });

onMounted(async () => {
  await loadData();
});
</script> 